// MorningBrief.jsx -- data-driven brief composed from primitives
// DEC-PD31-05: Screens emerge from primitives composed by agents.
// ASCII only. var declarations. ESM only. Inline styles.
import { useContext, useState } from 'react';
import { ThemeContext, MONO } from '../../lib/theme.js';
import { getProgressionIcon, getAutonomyIcon } from '../../lib/progression-icons.js';
import { MetricRow, BriefCard, ApprovalCard, TrainingCardBack, StatusUpdate, ProgressionTip, ProgressionEvent, HandledCard, LevelUpCard, ToolNudgeCard } from './Primitives.jsx';


function resolveColor(keyword, theme) {
  if (!keyword) return theme.accent;
  if (keyword === 'critical') return theme.critical;
  if (keyword === 'success')  return theme.success;
  if (keyword === 'warning')  return theme.warning;
  if (keyword === 'accent')   return theme.accent;
  return keyword;
}

export default function MorningBrief({ items, onAction, isDemo, onGenerateBrief }) {
  var theme = useContext(ThemeContext);
  var _flippedCard = useState(null);
  var flippedCard = _flippedCard[0];
  var setFlippedCard = _flippedCard[1];

  if (!items || items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 16px' }}>
        <div style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 6 }}>Your Brief is loading</div>
        <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.5 }}>Ask Hub &quot;What needs my attention?&quot; or &quot;Show me my Brief&quot; to generate cards.</div>
        {onGenerateBrief && (
          <div style={{ marginTop: 12 }}>
            <button onClick={onGenerateBrief} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid ' + theme.accent, background: 'transparent', color: theme.accent, fontSize: 12, fontFamily: theme.mono || 'monospace', cursor: 'pointer', fontWeight: 600 }}>Generate Brief</button>
          </div>
        )}
      </div>
    );
  }

  // Group status_update items by agent for HandledCard
  var handledByAgent = {};
  var approvalCards = [];
  var progressionCards = [];
  var metricRow = null;
  var otherCards = [];

  for (var i = 0; i < items.length; i++) {
    var item = items[i];
    if (item.type === 'greeting' || item.type === 'section_label') continue;
    if (item.type === 'status_update') {
      var agentKey = item.agent || 'Hub';
      var agentDisplay = agentKey.charAt(0).toUpperCase() + agentKey.slice(1);
      if (!handledByAgent[agentKey]) handledByAgent[agentKey] = { name: agentDisplay, color: resolveColor(item.color, theme), items: [] };
      handledByAgent[agentKey].items.push({ text: item.text });
      continue;
    }
    if (item.type === 'level_up') { progressionCards.unshift(item); continue; }
    if (item.type === 'tool_nudge') { otherCards.unshift(item); continue; }
    if (item.type === 'approval_card') { approvalCards.push(item); continue; }
    if (item.type === 'progression_tip' || item.type === 'progression_event') { progressionCards.push(item); continue; }
    if (item.type === 'metric_row') { metricRow = item; continue; }
    otherCards.push(item);
  }

  var handledAgents = Object.keys(handledByAgent).map(function(k) { return handledByAgent[k]; });

  return (
    <div>
      {/* Brief header */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Brief</div>
        <div style={{ fontSize: 10, color: theme.textMuted }}>Daily overview &middot; Live</div>
      </div>

      {/* 1. Handled Autonomously */}
      {handledAgents.length > 0 && <HandledCard agents={handledAgents} theme={theme} />}

      {/* 2. Approval cards */}
      {approvalCards.map(function(item, i) {
        if (flippedCard === i && item.trainingData) {
          return (
            <TrainingCardBack key={'ac_' + i}
              color={resolveColor(item.color, theme)}
              agent={item.agent}
              trainingData={item.trainingData}
              onFlipBack={function() { setFlippedCard(null); }}
            />
          );
        }
        return (
          <ApprovalCard key={'ac_' + i}
            color={resolveColor(item.color, theme)}
            agent={item.agent}
            title={item.title}
            body={item.body}
            actions={item.actions}
            level={item.level}
            onAction={onAction}
            trainingData={item.trainingData}
            nearPromotion={item.nearPromotion}
            ghostPreview={item.ghostPreview}
            onFlip={item.trainingData ? function() { setFlippedCard(i); } : undefined}
          />
        );
      })}

      {/* 3. Progression */}
      {progressionCards.map(function(item, i) {
        if (item.type === 'level_up') {
          return (
            <LevelUpCard key={'lu_' + i}
              theme={theme}
              agent={item.agent}
              level={item.level}
              changes={item.changes || []}
              stats={item.stats || ''}
            />
          );
        }
        if (item.type === 'progression_tip') {
          return (
            <ProgressionTip key={'pt_' + i}
              color={resolveColor(item.color, theme)}
              agent={item.agent}
              title={item.title}
              body={item.body}
              detail={item.detail}
              action={item.action}
              effort={item.effort}
              onAction={onAction}
            />
          );
        }
        var evtIcon = null;
        if (item.eventType === 'level_up') evtIcon = getAutonomyIcon(2);
        else if (item.eventType === 'lens_unlock') evtIcon = getProgressionIcon('lens_unlocked');
        else if (item.eventType === 'connector_link') evtIcon = getProgressionIcon('connector_linked');
        else evtIcon = getProgressionIcon('feature_unlocked');
        return (
          <ProgressionEvent key={'pe_' + i}
            icon={evtIcon}
            color={resolveColor(item.color, theme)}
            agent={item.agent}
            title={item.title}
            pills={item.pills || []}
            body={item.body}
            comparison={item.comparison}
          />
        );
      })}

      {/* 4. Financial Snapshot */}
      {metricRow && (
        <div style={{ background: theme.bg || '#f8f7f4', borderRadius: 8, padding: '10px 12px', border: '1px solid ' + theme.border, marginBottom: 4 }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.textMuted, marginBottom: 6 }}>Financial Snapshot</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {metricRow.items.map(function(m, mi) {
              return (
                <div key={mi}>
                  <div style={{ fontSize: 16, fontWeight: 700, fontFamily: MONO, color: m.color ? resolveColor(m.color, theme) : theme.text }}>{m.value}</div>
                  <div style={{ fontSize: 8, color: theme.textMuted }}>{m.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 5. Other cards */}
      {otherCards.map(function(item, i) {
        if (item.type === 'tool_nudge') {
          return (
            <ToolNudgeCard key={'tn_' + i}
              theme={theme}
              items={item.items || []}
              onAction={onAction}
            />
          );
        }
        if (item.type === 'brief_card') {
          return (
            <BriefCard key={'bc_' + i}
              color={resolveColor(item.color, theme)}
              agent={item.agent}
              title={item.title}
              body={item.body}
              actions={item.actions}
              onAction={onAction}
            />
          );
        }
        return null;
      })}
      {onGenerateBrief && (
        <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
          <button onClick={onGenerateBrief} style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid ' + (theme.accent || '#4F6EF7') + '40', background: 'transparent', color: theme.accent || '#4F6EF7', fontSize: 11, fontFamily: theme.mono || 'monospace', cursor: 'pointer', fontWeight: 600, opacity: 0.7 }}>Refresh Brief</button>
        </div>
      )}
    </div>
  );
}
